import { Navigate } from 'react-router';

import {
  useCurrentUser,
  useProfiles,
  useSubscriptionStatus,
  useUserDocument,
} from '@chartai/api-client';

import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';

interface PublicGuardProps {
  children: React.ReactNode;
}

export function PublicGuard({ children }: PublicGuardProps) {
  const { user, isLoading } = useCurrentUser();
  const { data: userDoc, isLoading: userDocLoading } = useUserDocument(user?.uid ?? null);
  const { data: profiles, isLoading: profilesLoading } = useProfiles(user?.uid ?? '');
  const sub = useSubscriptionStatus(user?.uid ?? '');

  if (isLoading || (user && (userDocLoading || profilesLoading || sub.isLoading))) {
    return <LoadingSpinner size="lg" className="min-h-screen" />;
  }

  if (user) {
    // CAB-517: a user who just finished the signup wizard is authenticated but
    // hasn't picked a plan yet — send them to the billing / plan-picker section
    // (where the Terms modal then shows) instead of /dashboard. The signup page's
    // own navigate('/subscriptions') used to be clobbered by this guard's
    // redirect; making the destination subscription-aware fixes that for good.
    //
    // "Just finished signup, no plan yet" = onboarding done, no active
    // subscription, AND no profile created. The no-profile check is what keeps an
    // established free-plan user (who already made a profile) going to their
    // dashboard rather than being bounced back to the plan picker on every login.
    const productTours = userDoc?.['productTours'] as Record<
      string,
      Record<string, unknown>
    > | null;
    const onboardingFinished =
      productTours?.['onboarding']?.['finished'] === true ||
      (Boolean(userDoc?.['dentalClinic']) && Boolean(userDoc?.['practiceSoftware']));
    const hasNoProfile = !profiles || profiles.length === 0;

    if (onboardingFinished && hasNoProfile && !sub.isActive) {
      return <Navigate to="/subscriptions" replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
